import { useContext } from 'react';

import { Node } from 'reactflow';

import { getColorsForUserId } from '@components/AvatarCircle';

import { AuthContext } from '@contexts/AuthContext';
import { useOptionalViewers } from '@contexts/ViewersContext';

import { NodeMetadata } from '@hooks/useNodeMetadata';

import { getFullName } from '@models/User';

/**
 * Formats a timestamp as a relative time string (e.g., "2 minutes ago")
 * @param timestamp The timestamp in seconds
 * @returns A formatted string representing the relative time
 */
export const formatRelativeTime = (timestamp: number): string => {
    const now = Math.floor(Date.now() / 1000);
    const secondsAgo = now - timestamp;

    if (secondsAgo < 60) {
        return `${secondsAgo} second${secondsAgo !== 1 ? 's' : ''} ago`;
    } else if (secondsAgo < 3600) {
        const minutes = Math.floor(secondsAgo / 60);
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (secondsAgo < 86400) {
        const hours = Math.floor(secondsAgo / 3600);
        return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
        const days = Math.floor(secondsAgo / 86400);
        return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
};

/**
 * Checks if a node was recently added or updated by another user
 * The indicator will automatically disappear after the specified time threshold
 * @param node The node to check
 * @param currentUserUuid The current user's UUID
 * @param recentTimeThresholdSeconds The time threshold in seconds (default: 90 seconds = 1.5 minutes)
 * @returns An object with node modification information
 */
export const useNodeCollaborationStatus = (recentTimeThresholdSeconds = 90) => {
    const { user } = useContext(AuthContext);
    const viewersContext = useOptionalViewers();
    const currentUserUuid = user?.uuid;

    /**
     * Checks if a node was recently modified by another user
     */
    const getNodeCollaborationStatus = (node: Node) => {
        // Get the list of viewers from context
        const users = viewersContext?.viewers || [];
        const metadata = node.data?.metadata as NodeMetadata | undefined;

        if (!metadata) {
            return {
                isRecentlyModified: false,
                modifierColor: null,
                modifierUuid: null,
                modifierName: null,
                modificationTime: null,
                isCreated: false,
                tooltipText: null,
            };
        }

        // Check if the node was modified by another user
        const isAnotherUser = metadata.user_uuid && metadata.user_uuid !== currentUserUuid;

        if (!isAnotherUser) {
            return {
                isRecentlyModified: false,
                modifierColor: null,
                modifierUuid: null,
                modifierName: null,
                modificationTime: null,
                isCreated: false,
                tooltipText: null,
            };
        }

        // Check if the node was recently modified
        const now = Math.floor(Date.now() / 1000);

        // Use updated_at if available, otherwise use created_at
        const isCreated = !metadata.updated_at || metadata.updated_at === metadata.created_at;
        const modificationTime = metadata.updated_at || metadata.created_at;
        const isRecent = modificationTime && now - modificationTime < recentTimeThresholdSeconds;

        if (!isRecent) {
            return {
                isRecentlyModified: false,
                modifierColor: null,
                modifierUuid: null,
                modifierName: null,
                modificationTime: null,
                isCreated: false,
                tooltipText: null,
            };
        }

        // Get the color for the user who modified the node
        const modifierUuid = metadata.user_uuid;
        const modifierColor = getColorsForUserId(modifierUuid)[0];

        // Try to find the user's name and avatar from the viewers list
        let modifierName = 'Unknown User';
        let modifierAvatarUrl = null;
        const userFromViewers = users.find((u) => u.uuid === modifierUuid);
        if (userFromViewers) {
            modifierName = getFullName(userFromViewers);
            modifierAvatarUrl = userFromViewers.avatar_url;
        }

        // Create tooltip text
        const timeAgo = formatRelativeTime(modificationTime);
        const action = isCreated ? 'Created' : 'Updated';
        const tooltipText = `${action} by ${modifierName} ${timeAgo}`;

        return {
            isRecentlyModified: true,
            modifierColor,
            modifierUuid,
            modifierName,
            modifierAvatarUrl,
            modificationTime,
            isCreated,
            tooltipText,
        };
    };

    return { getNodeCollaborationStatus };
};

/**
 * Creates styles for the modification indicator avatar container
 * @returns CSS styles for the avatar container
 */
export const getModificationIndicatorStyles = (): React.CSSProperties => {
    return {
        position: 'absolute' as const,
        top: '-25px',
        left: '0px',
        zIndex: 1000,
    };
};
